/**
 * ActivityLogScreen — Owner/Manager oversight of every number-bearing action.
 *
 * Reads the append-only `audit_logs` stream (written via @/utils/auditLog by
 * each mutation service) and renders a filterable, reactive timeline:
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ Search · Category filter · Date range                       │
 *   ├────────────────────────────────────────────────────────────┤
 *   │ Time │ User │ Category │ Action │ Detail          │ Amount  │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Route is gated by ACTIVITY_LOG_VIEW (Owner + Manager only). Note the log
 * is device-local — it reflects actions performed on this machine.
 */

import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import {
  Activity, Search, CalendarDays, ShoppingCart, Package, Boxes,
  Users, Building2, Truck, Clock,
} from 'lucide-react';
import { database } from '@/db';
import { AuditLog } from '@/db/models/System';
import User from '@/db/models/User';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  ACTION_CATEGORY, ACTION_LABEL, CATEGORY_LABEL,
  type AuditAction, type AuditCategory, type AuditChanges,
} from '@/utils/auditLog';

type CategoryFilter = 'ALL' | AuditCategory;
type RangeFilter = 'TODAY' | '7D' | '30D' | 'ALL';

const CATEGORY_ICON: Record<AuditCategory, typeof Activity> = {
  DAY_SESSION: Clock,
  BILLING: ShoppingCart,
  INVENTORY: Package,
  STOCK: Boxes,
  CUSTOMER: Users,
  SUPPLIER: Building2,
  PURCHASE: Truck,
};

const CATEGORY_TONE: Record<AuditCategory, string> = {
  DAY_SESSION: 'bg-accent/10 text-accent-ink',
  BILLING: 'bg-success/10 text-success-ink',
  INVENTORY: 'bg-bg-subtle text-fg-secondary',
  STOCK: 'bg-warning/10 text-warning-ink',
  CUSTOMER: 'bg-accent/10 text-accent-ink',
  SUPPLIER: 'bg-bg-subtle text-fg-secondary',
  PURCHASE: 'bg-warning/10 text-warning-ink',
};

/** Actions that belong to a given category — used to push filtering into the query. */
const CATEGORY_ACTIONS: Record<AuditCategory, AuditAction[]> = (() => {
  const map = {} as Record<AuditCategory, AuditAction[]>;
  for (const [action, cat] of Object.entries(ACTION_CATEGORY) as [AuditAction, AuditCategory][]) {
    (map[cat] ??= []).push(action);
  }
  return map;
})();

function rangeCutoff(range: RangeFilter): number | null {
  if (range === 'ALL') return null;
  const d = new Date();
  if (range === 'TODAY') {
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  const days = range === '7D' ? 7 : 30;
  return Date.now() - days * 24 * 60 * 60 * 1000;
}

function parseChanges(raw: string | null): AuditChanges {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as AuditChanges;
  } catch {
    return {};
  }
}

export function ActivityLogScreen() {
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [category, setCategory] = useState<CategoryFilter>('ALL');
  const [range, setRange] = useState<RangeFilter>('7D');

  // ── Reactive audit stream ────────────────────────────────
  const logs = useObservable<AuditLog[]>(
    () => {
      if (!merchantId) return null;
      const conds: any[] = [Q.where('merchant_id', merchantId)];
      const cutoff = rangeCutoff(range);
      if (cutoff != null) conds.push(Q.where('created_at', Q.gte(cutoff)));
      if (category !== 'ALL') conds.push(Q.where('action', Q.oneOf(CATEGORY_ACTIONS[category])));
      conds.push(Q.sortBy('created_at', Q.desc));
      conds.push(Q.take(1000));
      return database.get<AuditLog>('audit_logs').query(...conds).observe();
    },
    [merchantId, category, range],
    [],
  );

  // ── Decode + client-side text search ─────────────────────
  const rows = useMemo(() => {
    return logs
      .map((log) => {
        const action = log.action as AuditAction;
        const cat = ACTION_CATEGORY[action];
        const changes = parseChanges(log.changes);
        return {
          id: log.id,
          createdAt: log.createdAt,
          userId: log.userId,
          action,
          category: cat,
          label: ACTION_LABEL[action] ?? log.action,
          summary: changes.summary ?? '',
          amount: typeof changes.amount === 'number' ? changes.amount : null,
          quantity: typeof changes.quantity === 'number' ? changes.quantity : null,
        };
      })
      // Drop rows for actions we don't recognise (defensive — schema drift).
      .filter((r) => r.category !== undefined)
      .filter((r) => {
        if (!searchQuery) return true;
        return (
          r.label.toLowerCase().includes(searchQuery) ||
          r.summary.toLowerCase().includes(searchQuery)
        );
      });
  }, [logs, searchQuery]);

  // ── User name lookup ─────────────────────────────────────
  const userIds = useMemo(
    () => Array.from(new Set(rows.map((r) => r.userId).filter((x): x is string => !!x))),
    [rows],
  );
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (userIds.length === 0) { setUserNames({}); return; }
    let cancelled = false;
    (async () => {
      const map: Record<string, string> = {};
      for (let i = 0; i < userIds.length; i += 500) {
        const batch = userIds.slice(i, i + 500);
        const users = await database.get<User>('users').query(Q.where('id', Q.oneOf(batch))).fetch();
        for (const u of users) map[u.id] = u.name?.trim() || u.phone || 'User';
      }
      if (!cancelled) setUserNames(map);
    })();
    return () => { cancelled = true; };
  }, [userIds]);

  const categoryOptions: { value: CategoryFilter; label: string }[] = [
    { value: 'ALL', label: 'All activity' },
    ...(Object.keys(CATEGORY_LABEL) as AuditCategory[]).map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
  ];

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Activity size={18} className="text-accent-ink" strokeWidth={1.75} />
        <h1 className="text-heading font-semibold text-fg-primary">Activity Log</h1>
        <span className="ml-2 text-label uppercase text-fg-tertiary">This device</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="relative min-w-0 basis-full sm:basis-auto sm:flex-1">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search activity…"
            className="h-9 pl-9"
          />
        </div>
        <FilterChip<CategoryFilter>
          icon={<Activity size={12} />}
          options={categoryOptions}
          value={category}
          onChange={setCategory}
        />
        <FilterChip<RangeFilter>
          icon={<CalendarDays size={12} />}
          options={[
            { value: 'TODAY', label: 'Today' },
            { value: '7D', label: 'Last 7 days' },
            { value: '30D', label: 'Last 30 days' },
            { value: 'ALL', label: 'All time' },
          ]}
          value={range}
          onChange={setRange}
        />
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        <table className="w-full text-body">
          <thead className="sticky top-0 bg-bg-surface text-label uppercase text-fg-tertiary">
            <tr className="border-b border-stroke-hairline/10">
              <th className="px-4 py-2 text-left font-normal">When</th>
              <th className="px-4 py-2 text-left font-normal">By</th>
              <th className="px-4 py-2 text-left font-normal">Category</th>
              <th className="px-4 py-2 text-left font-normal">Action</th>
              <th className="px-4 py-2 text-left font-normal">Detail</th>
              <th className="px-4 py-2 text-right font-normal">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-fg-tertiary">
                  No activity recorded for these filters yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const Icon = CATEGORY_ICON[r.category];
                return (
                  <tr key={r.id} className="border-b border-stroke-hairline/10 hover:bg-bg-subtle">
                    <td className="whitespace-nowrap px-4 py-2.5 text-fg-secondary tabular-nums">
                      {r.createdAt.toLocaleString(undefined, {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-2.5 text-fg-secondary">
                      {r.userId ? (userNames[r.userId] ?? '…') : <span className="text-fg-tertiary">System</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-label uppercase', CATEGORY_TONE[r.category])}>
                        <Icon size={11} strokeWidth={2} />
                        {CATEGORY_LABEL[r.category]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-medium text-fg-primary">{r.label}</td>
                    <td className="px-4 py-2.5 text-fg-secondary">{r.summary || '—'}</td>
                    <td className={cn(
                      'whitespace-nowrap px-4 py-2.5 text-right tabular-nums',
                      r.amount == null ? 'text-fg-tertiary' : r.amount < 0 ? 'text-danger-ink' : 'text-fg-primary',
                    )}>
                      {r.amount == null ? '—' : formatCurrency(r.amount)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Pieces
// ═════════════════════════════════════════════════════════════════════

function FilterChip<T extends string>({
  icon,
  options,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const current = options.find((o) => o.value === value)?.label ?? '';
  return (
    <label className="relative inline-flex h-9 items-center gap-2 rounded-md border border-stroke-hairline/15 bg-bg-surface px-3 text-body text-fg-secondary cursor-pointer transition-colors hover:border-accent/40 hover:text-fg-primary">
      <span className="text-fg-tertiary">{icon}</span>
      <span>{current}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="absolute inset-0 cursor-pointer opacity-0"
        aria-label="Filter"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
